import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Badge,
  Menu,
  MenuItem,
  Container,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemIcon,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
  Paper,
  ListItemButton
} from '@mui/material';
import {
  Menu as MenuIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  Favorite as FavoriteIcon,
  Compare as CompareIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import { useStoreSettings } from '../../contexts/StoreSettingsContext';

interface NewStoreHeaderProps {
  onSearch?: (query: string) => void;
  onMobileMenuOpen?: () => void;
}

// نموذج للأقسام
const categories = [
  {
    id: 1,
    name: 'الإلكترونيات',
    subcategories: [
      { id: 101, name: 'الهواتف الذكية' },
      { id: 102, name: 'الحواسيب' },
      { id: 103, name: 'الأجهزة اللوحية' },
      { id: 104, name: 'الساعات الذكية' },
      { id: 105, name: 'الكاميرات' }
    ]
  },
  {
    id: 2,
    name: 'الملابس',
    subcategories: [
      { id: 201, name: 'رجالي' },
      { id: 202, name: 'نسائي' },
      { id: 203, name: 'أطفال' },
      { id: 204, name: 'أحذية' },
      { id: 205, name: 'إكسسوارات' }
    ]
  },
  {
    id: 3,
    name: 'المنزل والحديقة',
    subcategories: [
      { id: 301, name: 'أثاث' },
      { id: 302, name: 'أدوات المطبخ' },
      { id: 303, name: 'الحديقة' },
      { id: 304, name: 'الإضاءة' },
      { id: 305, name: 'الديكور' }
    ]
  },
  {
    id: 4,
    name: 'الرياضة',
    subcategories: [
      { id: 401, name: 'ملابس رياضية' },
      { id: 402, name: 'معدات رياضية' },
      { id: 403, name: 'أحذية رياضية' },
      { id: 404, name: 'رياضات مائية' },
      { id: 405, name: 'رياضات خارجية' }
    ]
  },
  {
    id: 5,
    name: 'الجمال والعناية الشخصية',
    subcategories: [
      { id: 501, name: 'العطور' },
      { id: 502, name: 'مستحضرات التجميل' },
      { id: 503, name: 'العناية بالبشرة' },
      { id: 504, name: 'العناية بالشعر' },
      { id: 505, name: 'العناية الشخصية' }
    ]
  }
];

const NewStoreHeader: React.FC<NewStoreHeaderProps> = ({ onSearch, onMobileMenuOpen }) => {
  const { settings } = useStoreSettings();
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCategoryId, setOpenCategoryId] = useState<number | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // استخراج اسم المتجر واللوجو من الإعدادات
  const storeName = settings?.general?.storeName || 'اليكترونيات السواح';
  const storeLogo = settings?.general?.storeLogo;

  // فتح قائمة الأقسام
  const handleCategoryMouseEnter = (_event: React.MouseEvent<HTMLElement>, categoryId: number) => {
    setOpenCategoryId(categoryId);
  };

  // إغلاق قائمة الأقسام
  const handleCategoryMouseLeave = () => {
    setOpenCategoryId(null);
  };

  // فتح قائمة المستخدم
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // إغلاق قائمة المستخدم
  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  // البحث
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchQuery);
    }
  };

  // فتح القائمة الجانبية للجوال
  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    if (onMobileMenuOpen) {
      onMobileMenuOpen();
    }
  };

  return (
    <Box className="store-header" sx={{ flexGrow: 1 }}>
      {/* حقل البحث في أعلى الهيدر */}
      <Box 
        className="search-top-bar"
        sx={{ 
          width: '100%', 
          backgroundColor: 'background.paper',
          py: 1.5,
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%'
            }}
          >
            {/* مثال لاستخدام LiveSearch في المستقبل
            <LiveSearch
              onSearch={onSearch}
              placeholder="ابحث عن منتجات..."
              sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '80%', md: '60%', lg: '50%' }
              }}
            />
            */}
            <Box className="search-field" sx={{ 
              position: 'relative', 
              borderRadius: '24px', 
              bgcolor: 'background.paper', 
              border: '1px solid',
              borderColor: 'divider',
              width: '100%', 
              maxWidth: { xs: '100%', sm: '80%', md: '60%', lg: '50%' },
              '&:hover': {
                boxShadow: '0 0 8px rgba(0,0,0,0.1)'
              }
            }}>
              <IconButton
                type="submit"
                sx={{ p: '10px', position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', color: 'primary.main' }}
                aria-label="بحث"
              >
                <SearchIcon />
              </IconButton>
              <InputBase
                sx={{ 
                  ml: 1, 
                  flex: 1, 
                  width: '100%', 
                  p: '8px 16px',
                  color: 'text.primary',
                  '& ::placeholder': {
                    color: 'text.secondary',
                    opacity: 0.8
                  }
                }}
                placeholder="ابحث عن منتجات..."
                inputProps={{ 'aria-label': 'ابحث عن منتجات' }}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* الهيدر العلوي */}
      <AppBar position="static" className="store-header-top" sx={{
        bgcolor: 'var(--header-top-background, #f5f5f5)',
        color: 'var(--header-top-text, #333)',
        boxShadow: 1,
        width: '100%'
      }}>
        <Container maxWidth={false} sx={{ px: { xs: 2, sm: 3, md: 4, lg: 5 } }}>
          <Toolbar>
            {/* زر القائمة للجوال */}
            {isMobile && (
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="القائمة"
                onClick={handleMobileMenuToggle}
                sx={{ mr: 2 }}
              >
                <MenuIcon />
              </IconButton>
            )}

            {/* شعار المتجر */}
            {storeLogo ? (
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  mr: 2,
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  textDecoration: 'none',
                }}
                className="store-logo"
              >
                <Box
                  component="img"
                  src={storeLogo}
                  alt={storeName}
                  sx={{
                    height: 40,
                    mr: 1
                  }}
                />
              </Box>
            ) : (
              <Typography
                variant="h6"
                noWrap
                component={RouterLink}
                to="/"
                sx={{
                  mr: 2,
                  display: { xs: 'none', md: 'flex' },
                  fontWeight: 700,
                  color: 'inherit',
                  textDecoration: 'none',
                }}
                className="store-logo"
              >
                {storeName}
              </Typography>
            )}

            {/* قائمة الأقسام */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, mr: 2 }}>
              <Button
                component={RouterLink}
                to="/"
                color="inherit"
                sx={{
                  mx: 0.5,
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.05)'
                  }
                }}
              >
                الرئيسية
              </Button>

              <Button
                component={RouterLink}
                to="/offers"
                color="inherit"
                sx={{
                  mx: 0.5,
                  fontWeight: 'bold',
                  '&:hover': {
                    backgroundColor: 'rgba(0,0,0,0.05)'
                  }
                }}
              >
                العروض
              </Button>

              {categories.map((category) => (
                <Box
                  key={category.id}
                  onMouseEnter={(e) => handleCategoryMouseEnter(e, category.id)}
                  onMouseLeave={handleCategoryMouseLeave}
                  sx={{ position: 'relative' }}
                >
                  <Button
                    color="inherit"
                    sx={{
                      mx: 0.5,
                      fontWeight: 'bold',
                      '&:hover': {
                        backgroundColor: 'rgba(0,0,0,0.05)'
                      }
                    }}
                    endIcon={<KeyboardArrowDownIcon />}
                  >
                    {category.name}
                  </Button>

                  {/* القائمة المنسدلة للأقسام الفرعية */}
                  <Collapse
                    in={openCategoryId === category.id}
                    timeout="auto"
                    sx={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      zIndex: 1000,
                      width: 220,
                      boxShadow: 3,
                      bgcolor: 'background.paper',
                      borderRadius: 1
                    }}
                  >
                    <Paper sx={{ p: 1 }}>
                      <List dense component="nav" disablePadding>
                        {category.subcategories.map((subcat) => (
                          <ListItem
                            key={subcat.id}
                            component={RouterLink}
                            to={`/category/${category.id}/${subcat.id}`}
                            sx={{
                              py: 0.5,
                              borderRadius: 1,
                              '&:hover': {
                                backgroundColor: 'rgba(0,0,0,0.05)'
                              },
                              textDecoration: 'none',
                              color: 'text.primary'
                            }}
                          >
                            <ListItemText primary={subcat.name} />
                          </ListItem>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <ListItem
                          component={RouterLink}
                          to={`/category/${category.id}`}
                          sx={{
                            py: 0.5,
                            borderRadius: 1,
                            '&:hover': {
                              backgroundColor: 'rgba(0,0,0,0.05)'
                            },
                            textDecoration: 'none',
                            color: 'primary.main',
                            fontWeight: 'bold'
                          }}
                        >
                          <ListItemText primary={`جميع ${category.name}`} />
                        </ListItem>
                      </List>
                    </Paper>
                  </Collapse>
                </Box>
              ))}
            </Box>

            {/* أيقونات الهيدر العلوي */}
            <Box className="header-icons" sx={{ display: 'flex', alignItems: 'center' }}>
              {/* أيقونة المفضلة */}
              <IconButton
                size="large"
                aria-label="المفضلة"
                color="inherit"
                component={RouterLink}
                to="/wishlist"
                className="header-icon"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Badge badgeContent={3} color="secondary">
                  <FavoriteIcon />
                </Badge>
              </IconButton>

              {/* أيقونة المقارنة */}
              <IconButton
                size="large"
                aria-label="المقارنة"
                color="inherit"
                component={RouterLink}
                to="/compare"
                className="header-icon"
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                <Badge badgeContent={2} color="secondary">
                  <CompareIcon />
                </Badge>
              </IconButton>

              {/* أيقونة السلة */}
              <IconButton
                size="large"
                aria-label="سلة التسوق"
                color="inherit"
                component={RouterLink}
                to="/cart"
                className="header-icon"
              >
                <Badge badgeContent={4} color="secondary">
                  <ShoppingCartIcon />
                </Badge>
              </IconButton>

              {/* أيقونة المستخدم */}
              <IconButton
                size="large"
                aria-label="الحساب"
                aria-controls="menu-account"
                aria-haspopup="true"
                onClick={handleUserMenuOpen}
                color="inherit"
                className="header-icon"
              >
                <PersonIcon />
              </IconButton>
              <Menu
                id="menu-account"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleUserMenuClose}
              >
                <MenuItem onClick={handleUserMenuClose} component={RouterLink} to="/account">حسابي</MenuItem>
                <MenuItem onClick={handleUserMenuClose} component={RouterLink} to="/orders">طلباتي</MenuItem>
                <MenuItem onClick={handleUserMenuClose} component={RouterLink} to="/login">تسجيل الدخول</MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* القائمة الجانبية للجوال */}
      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuToggle}
        sx={{ '& .MuiDrawer-paper': { width: 280 } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {storeName}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <List>
            {categories.map((category) => (
              <React.Fragment key={category.id}>
                <ListItemButton onClick={() => setOpenCategoryId(openCategoryId === category.id ? null : category.id)}>
                  <ListItemIcon>
                    <CategoryIcon />
                  </ListItemIcon>
                  <ListItemText primary={category.name} />
                  {openCategoryId === category.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
                <Collapse in={openCategoryId === category.id} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {category.subcategories.map((subcat) => (
                      <ListItemButton
                        key={subcat.id}
                        component={RouterLink}
                        to={`/category/${category.id}/${subcat.id}`}
                        sx={{ pl: 4 }}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <ListItemText primary={subcat.name} />
                      </ListItemButton>
                    ))}
                    <ListItemButton
                      component={RouterLink}
                      to={`/category/${category.id}`}
                      sx={{ pl: 4, color: 'primary.main' }}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <ListItemText primary={`جميع ${category.name}`} />
                    </ListItemButton>
                  </List>
                </Collapse>
              </React.Fragment>
            ))}
          </List>

          <Divider sx={{ my: 2 }} />

          <List>
            <ListItemButton component={RouterLink} to="/">
              <ListItemText primary="الرئيسية" />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/offers">
              <ListItemText primary="العروض" />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/new-arrivals">
              <ListItemText primary="وصل حديثاً" />
            </ListItemButton>
            <ListItemButton component={RouterLink} to="/contact">
              <ListItemText primary="اتصل بنا" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default NewStoreHeader;
